import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RealtimeMonitor = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [liveStats, setLiveStats] = useState({
    activeAgents: 24,
    totalAgents: 28,
    callsInQueue: 7,
    longestWait: '2:34',
    avgWaitTime: '1:12',
    callsAnswered: 156,
    callsAbandoned: 8,
    serviceLevel: 94.2
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      
      // Simulate real-time data updates
      setLiveStats(prev => ({
        ...prev,
        callsInQueue: Math.max(0, prev?.callsInQueue + Math.floor(Math.random() * 3) - 1),
        callsAnswered: prev?.callsAnswered + Math.floor(Math.random() * 2),
        avgWaitTime: `${Math.floor(Math.random() * 3) + 1}:${Math.floor(Math.random() * 60)?.toString()?.padStart(2, '0')}`
      }));
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const queueAlerts = [
    {
      id: 1,
      type: 'warning',
      message: 'Kuyrukta bekleyen çağrı sayısı yüksek',
      timestamp: '14:32',
      priority: 'medium'
    },
    {
      id: 2,
      type: 'info',
      message: 'Yeni temsilci giriş yaptı: Elif K.',
      timestamp: '14:28',
      priority: 'low'
    },
    {
      id: 3,
      type: 'success',
      message: 'Hizmet seviyesi hedefine ulaşıldı',
      timestamp: '14:25',
      priority: 'low'
    }
  ];

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return 'AlertTriangle';
      case 'success': return 'CheckCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Live Statistics */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Anlık İstatistikler</h3>
            <p className="text-sm text-slate-600">
              Son güncelleme: {currentTime?.toLocaleTimeString('tr-TR')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-600">Canlı</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Users" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-slate-600">Aktif Temsilci</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {liveStats?.activeAgents}/{liveStats?.totalAgents}
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Phone" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-slate-600">Kuyrukta</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {liveStats?.callsInQueue}
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} color="var(--color-error)" />
              <span className="text-sm font-medium text-slate-600">En Uzun Bekleme</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {liveStats?.longestWait}
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Timer" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-slate-600">Ort. Bekleme</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {liveStats?.avgWaitTime}
            </div>
          </div>
        </div>

        {/* Service Level Indicator */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Hizmet Seviyesi</span>
            <span className="text-lg font-bold text-green-600">{liveStats?.serviceLevel}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${liveStats?.serviceLevel}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>Hedef: 90%</span>
            <span>Mükemmel: 95%+</span>
          </div>
        </div>
      </div>
      {/* Queue Alerts */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sistem Uyarıları</h3>
            <p className="text-sm text-slate-600">Anlık durum bildirimleri</p>
          </div>
          <button className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
            <Icon name="Settings" size={16} />
          </button>
        </div>

        <div className="space-y-3">
          {queueAlerts?.map((alert) => (
            <div 
              key={alert?.id}
              className={`flex items-start space-x-3 p-3 rounded-lg border ${getAlertColor(alert?.type)}`}
            >
              <Icon name={getAlertIcon(alert?.type)} size={16} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{alert?.message}</p>
                <p className="text-xs opacity-75 mt-1">{alert?.timestamp}</p>
              </div>
              <button className="p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors">
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-4 border-t border-slate-200">
          <h4 className="text-sm font-medium text-slate-900 mb-3">Hızlı İşlemler</h4>
          <div className="grid grid-cols-2 gap-2">
            <button className="flex items-center justify-center space-x-2 p-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              <Icon name="UserPlus" size={14} />
              <span>Temsilci Ekle</span>
            </button>
            <button className="flex items-center justify-center space-x-2 p-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              <Icon name="PhoneForwarded" size={14} />
              <span>Çağrı Yönlendir</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtimeMonitor;